import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Heart, Loader2, X } from 'lucide-react';
import Navigation from '../components/Navigation';
import ChannelCard from '../components/ChannelCard';
import VideoPlayer from '../components/VideoPlayer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FavoritesPage = () => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChannel, setSelectedChannel] = useState(null);

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/favorites`, {
        withCredentials: true
      });
      setFavorites(response.data.favorites);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFavorites();
  }, []);

  const handleRemoveFavorite = async (channel) => {
    try {
      await axios.delete(`${API_URL}/api/favorites/${channel.id}`, {
        withCredentials: true
      });
      setFavorites(prev => prev.filter(f => f.id !== channel.id));
    } catch (error) {
      console.error('Error removing favorite:', error);
    }
  };

  const handleChannelClick = (channel) => {
    setSelectedChannel(channel);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navigation />
      
      <main className="pt-20 pb-12">
        {/* Video Player Modal */}
        {selectedChannel && (
          <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" data-testid="player-modal">
            <div className="w-full max-w-6xl">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold font-['Outfit']">{selectedChannel.name}</h2>
                <button
                  onClick={() => setSelectedChannel(null)}
                  className="p-2 text-[#A3A3A3] hover:text-white hover:bg-[#1A1A1A] rounded-lg transition-colors"
                  data-testid="close-player-btn"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <VideoPlayer url={selectedChannel.url} channelName={selectedChannel.name} />
            </div>
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <Heart className="w-8 h-8 text-[#E50914]" fill="currentColor" />
            <h1 className="text-3xl font-bold font-['Outfit']">My Favorites</h1>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-10 h-10 text-[#E50914] animate-spin" />
            </div>
          ) : favorites.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <Heart className="w-16 h-16 text-[#262626] mb-4" strokeWidth={1} />
              <h3 className="text-xl font-semibold mb-2 font-['Outfit']">No Favorites Yet</h3>
              <p className="text-[#A3A3A3] max-w-md">
                Start adding channels to your favorites by clicking the heart icon on any channel.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="favorites-grid">
              {favorites.map((channel) => (
                <ChannelCard
                  key={channel.id}
                  channel={channel}
                  onClick={handleChannelClick}
                  onFavorite={handleRemoveFavorite}
                  isFavorite={true}
                />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default FavoritesPage;
